import Dropdown from 'react-bootstrap/Dropdown';
import IconButton from '@material-ui/core/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';
import { getAllNotifications, updateNotification } from '../../store/actions/notifications.action';
import InfiniteScroll from "react-infinite-scroll-component";
import moment from 'moment';
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { NOTIFICATION_FETCH_INTERVAL } from '../../constant';

const CartBadge = ({ count }) => (
    <span className="badge">
        {count}
    </span>
);

const Notifications = () => {
    const dispatch = useDispatch();
    const { notficationsList, notficationsListStatus } = useSelector(state => state.notificationReducer);
    const [displayedNotifications, setDisplayedNotifications] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [previousCount, setPreviousCount] = useState(0);
    const itemsPerPage = 10;
    const intervalRef = useRef(null);

    const fetchNotifications = (pageNumber = 1) => {
        dispatch(getAllNotifications({ page: pageNumber, limit: itemsPerPage }));
    };

    useEffect(() => {
        if (notficationsList?.length > 0) {
            const initialNotifications = notficationsList.slice(0, itemsPerPage);
            setDisplayedNotifications(initialNotifications);
            setHasMore(notficationsList.length > itemsPerPage);
            setPage(1);
            setPreviousCount(notficationsList.length);
        }
    }, [notficationsList]);

    // Function to check for new notifications and update count
    const checkForNewNotifications = () => {
        const currentCount = notficationsList.length || 0;
        if (currentCount > previousCount) {
            // New notifications available, update the displayed list
            const initialNotifications = notficationsList.slice(0, itemsPerPage);
            setDisplayedNotifications(initialNotifications);
            setHasMore(notficationsList.length > itemsPerPage);
            setPage(1); // Reset to first page
        }
        setPreviousCount(currentCount);
    };

    // Auto-fetch notifications every 3 minutes
    const startAutoFetch = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
            fetchNotifications();
        }, NOTIFICATION_FETCH_INTERVAL);
    };

    // Stop auto-fetch when component unmounts
    const stopAutoFetch = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const fetchMoreData = () => {
        if (displayedNotifications.length >= notficationsList?.length) {
            setHasMore(false);
            return;
        }

        const nextPage = page + 1;
        const startIndex = (nextPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const newNotifications = notficationsList.slice(startIndex, endIndex);

        if (newNotifications.length === 0) {
            setHasMore(false);
        } else {
            setDisplayedNotifications(prev => [...prev, ...newNotifications]);
            setPage(nextPage);
        }
    };

    const renderNotificationItem = (notification) => {
        return (
            <Dropdown.Item key={notification.Id} href="#">
                <div className="notification-item">
                    <div className="notification-icon">
                        <i className={notification.icon || "fa fa-bell mt-2"} aria-hidden="true"></i>
                    </div>
                    <div className="notification-content">
                        <p>{notification.fileName}</p>
                        {notification.createdDate && (
                            <>
                                <small className="text-muted pr-3">{notification.moduleType}</small>
                                <small className="text-muted pr-3">
                                    {moment(notification.createdDate).format("MM-DD-YYYY | h:mm:ss A")}
                                </small>
                            </>
                        )}
                        {notification.downloadLink && (
                            <button className='link downloadReport sortBtn' onClick={(e) => handleDownloadFile(e, notification)}>Download</button>
                        )}
                        {notification.moduleType === 'Outbound' && (
                            <button className='link downloadReport sortBtn' onClick={(e) => handleReadNotification(e, notification)}>Mark as Read</button>
                        )}
                    </div>
                </div>
            </Dropdown.Item>
        );
    };

    const handleDownloadFile = async (event, notification) => {
        window.open(notification.downloadLink, '_blank');
        toast.success('File downloaded successfully');
        let payload = {
            id: notification.Id,
            isDownloaded: true
        }
        let updateNotificationRes = await updateNotification(payload);
        if (updateNotificationRes?.data?.Status === 200) {
            setDisplayedNotifications(prev => prev.filter(item => item.Id !== notification.Id));
            fetchNotifications();
        } else {
            console.log('Failed to update notification');
        }
    };

    const handleReadNotification = async (event, notification) => {
        let payload = {
            id: notification.Id,
            isDownloaded: true
        }
        let updateNotificationRes = await updateNotification(payload);
        if (updateNotificationRes?.data?.Status === 200) {
            setDisplayedNotifications(prev => prev.filter(item => item.Id !== notification.Id));
            globalThis.location.reload();
            fetchNotifications();
        } else {
            console.log('Failed to update notification');
        }
    };

    // Handle dropdown toggle to scroll to top when opened
    const handleDropdownToggle = (isOpen) => {
        if (isOpen) {
            // Use setTimeout to ensure the dropdown is rendered before scrolling
            setTimeout(() => {
                const scrollableDiv = document.getElementById('scrollableDiv');
                if (scrollableDiv) {
                    scrollableDiv.scrollTop = 0;
                }
            }, 10);
        }
    };

    useEffect(() => {
        fetchNotifications();
        startAutoFetch();

        // Cleanup interval on component unmount
        return () => {
            stopAutoFetch();
        };
    }, []);

    // Update displayed notifications when notficationsList changes
    useEffect(() => {
        if (notficationsList?.length > 0) {
            checkForNewNotifications();
        }
    }, [notficationsList]);

    return (
        <Dropdown className="d-inline" onToggle={handleDropdownToggle}>
            <Dropdown.Toggle as={IconButton} className="notificationIcon mr-2" id="dropdown-custom-components">
                <i className="fa fa-bell" aria-hidden="true"></i>
                {notficationsList?.length > 0 && (
                    <CartBadge count={notficationsList.length} />
                )}
            </Dropdown.Toggle>

            <Dropdown.Menu align={'top-right'} className='notificationDropdown'>
                <p className='headNotification pl-3 mb-3'>Notifications</p>

                {notficationsList && notficationsList.length === 0 && notficationsListStatus !== 'PENDING' ? (
                    <Dropdown.Item>
                        <div className="text-center text-muted p-3">
                            No notifications available
                        </div>
                    </Dropdown.Item>
                ) : (
                    <div id="scrollableDiv" style={{ height: 400, overflow: 'auto' }}>
                        <InfiniteScroll
                            dataLength={displayedNotifications.length}
                            next={fetchMoreData}
                            hasMore={hasMore}
                            loader={
                                <div className="text-center p-2">
                                    <i className="fa fa-spinner fa-spin"></i> Loading...
                                </div>
                            }
                            scrollableTarget="scrollableDiv"
                            endMessage={
                                displayedNotifications.length > 0 && (
                                    <div className="text-center text-muted p-2">
                                        <small>No more notifications</small>
                                    </div>
                                )
                            }
                        >
                            {displayedNotifications.map((notification, index) =>
                                renderNotificationItem(notification)
                            )}
                        </InfiniteScroll>
                    </div>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
}

CartBadge.propTypes = {
    count: PropTypes.number,
};

export default Notifications;